import { Component } from '@angular/core';
import { MobileAwareComponent } from '../../common/mobile-aware/mobile-aware.component';
import { localizeRole, Role } from '../../model/auth/Role';
import { User } from '../../model/auth/User';
import { Contest } from '../../model/contest/Contest';
import { Page } from '../../model/Page';
import { ContestService } from '../../service/contest.service';
import { TenantService } from '../../service/tenant.service';
import { MatDialog } from '@angular/material/dialog';
import { BreakpointObserver } from '@angular/cdk/layout';
import { NotificationHandler } from '../../helper/notification.handler';
import {
  InviteUserDetailsData,
  InviteUserModalComponent,
  InviteUserResult
} from '../../profile/invite-user-modal/invite-user-modal.component';
import {
  DeleteConfirmationData,
  DeleteConfirmationModalComponent,
  DeleteContext
} from '../../common/modal/delete-confirmation-modal/delete-confirmation-modal.component';
import { Region } from '../../model/location/Region';
import { AuthService } from '../../service/auth.service';
import { SchoolSelectResult } from '../../profile/school-select/school-select.component';
import { School, SchoolType } from '../../model/location/School';
import { PaymentStatus } from '../../model/Payment';
import { map } from 'rxjs/operators';
import { UserDetails, UserRequestDetails, UsersTableComponent } from '../../profile/users-table/users-table.component';

@Component({
  selector: 'app-teachers-list',
  templateUrl: './teachers-list.component.html',
  styleUrls: ['./teachers-list.component.scss']
})
export class TeachersListComponent extends MobileAwareComponent {

  readonly TEACHER_ROLE = Role.Teacher;

  readonly roles = [Role.Host, Role.Coordinator];

  inProgress = false;

  competitionId?: string;
  contest?: Contest;
  contestOpenForEnrol = false;

  teachers!: User[] | null;
  hasLocallyAdded = false;

  region?: Region;
  schoolType?: SchoolType;
  school?: School;
  allowRegionSelect = false;

  pendingRequests?: User[];

  page: Page<never> = {
    max_items: 20,
    asc: true
  };

  constructor(private contestService: ContestService,
              private tenantService: TenantService,
              private authService: AuthService,
              private dialog: MatDialog,
              breakpointObserver: BreakpointObserver) {
    super(breakpointObserver);
  }

  async initContent() {
    const tenant = this.tenantService.getTenant();
    if (tenant && tenant.id) {
      this.competitionId = tenant.id;
    }

    const user = await this.authService.getUser();
    if (user && user.region) {
      this.region = new Region(user.region, '');
    }
  }

  callerRolesUpdated(roles: Role[]) {
    this.allowRegionSelect = roles.includes(Role.Host);
  }

  contestSelected(contest: Contest) {
    this.contest = contest;
    this.loadPendingApproval(this.competitionId, this.contest, this.region, this.school);
    this.loadTeachers(this.competitionId, this.contest, this.region, this.school, true);
  }

  private loadPendingApproval(competitionId?: string, contest?: Contest, region?: Region, school?: School) {
    if (!competitionId || !contest || !region) return;

    if (contest.endsAt && contest.endsAt > new Date()) {
      this.pendingRequests = undefined;
      const schoolId = school ? school.id : undefined;
      this.contestService.getPayments(competitionId, contest.id, region.id, this.TEACHER_ROLE, PaymentStatus.review, schoolId)
        .pipe(map(page => page.content || []))
        .subscribe({
          next: teachers => {
            this.pendingRequests = teachers || [];
          },
          error: error => {
            NotificationHandler.handleError($localize`:Notifications|Fetching requests for review has failed message@@notifications-fetching-requests-for-review-failed-error:Fetching requests for review has failed`, error);
          }
        });
    } else {
      this.pendingRequests = [];
    }
  }

  loadTeachers(competitionId?: string, contest?: Contest, region?: Region, school?: School, reset = false) {
    if (!competitionId || !contest || !region) return;

    this.inProgress = true;

    if (reset) {
      this.page = {
        max_items: 20,
        asc: true
      };
      this.teachers = null;
    }

    this.contestService.getEnrolledUsers(competitionId, contest.id, Role.Teacher, this.page, region, school).subscribe({
      next: page => {
        const data = (page.content || []).filter(teacher => teacher.paymentStatus == PaymentStatus.successful);
        this.teachers = !this.teachers ? data : this.teachers.concat(data);
        this.page.next_token = page.next_token;
        this.inProgress = false;
      },
      error: error => {
        this.inProgress = false;
        NotificationHandler.handleError($localize`:Notifications|Contest teachers fetch has failed message@@notifications-contest-teachers-fetch-failed-error:Contest teachers fetch has failed`, error);
      }
    });
  }

  loadNextPage() {
    this.loadTeachers(this.competitionId, this.contest, this.region, this.school);
  }

  inviteTeacher(contest?: Contest | null) {
    if (this.competitionId && contest) {
      this.inviteTeacherInternal({
        competitionId: this.competitionId,
        contest: contest,
        requestedRole: Role.Teacher,
        region: this.region || undefined,
        school: this.school,
        schoolType: this.schoolType
      });
    }
  }

  private inviteTeacherInternal(data: InviteUserDetailsData) {
    this.dialog.open(InviteUserModalComponent, {
      width: '600px',
      data: data
    }).afterClosed().subscribe((data: InviteUserResult) => {
      if (data && data.addMore && data.addMoreData) {
        if (data.user && this.teachers
          && (this.region && data.user.region == this.region.id)
          && (!this.school || this.school.id == data.user.schoolId)) {
          this.teachers.unshift(data.user);
          this.hasLocallyAdded = true;
        }

        this.inviteTeacherInternal(data.addMoreData);
      } else if ((data && data.user) || this.hasLocallyAdded) {
        this.loadTeachers(this.competitionId, this.contest, this.region, this.school, true);
        this.hasLocallyAdded = false;
      }
    });
  }

  removeTeacher(details: UserDetails, usersTable: UsersTableComponent) {
    if (details.competitionId) {
      const data: DeleteConfirmationData = {
        title: $localize`:General|Remove action label@@general-remove-action-label:Remove`,
        itemType: localizeRole(Role.Teacher),
        name: details.user.email,
        actionDesc: $localize`:Teachers List|Teachers List delete item prompt@@teachers-list-delete-item-prompt:will be removed from the contest in a school`,
        archive: false
      };

      const dialogRef = this.dialog.open(DeleteConfirmationModalComponent, {
        width: '600px',
        data: data
      });

      dialogRef.componentInstance.deleteConfirmed.subscribe((context: DeleteContext) => {
        if (this.contest) {
          this.contestService.deleteUser(details.competitionId, this.contest.id, Role.Teacher, details.user.id).subscribe({
            next: () => {
              if (this.teachers) {
                const index = this.teachers.findIndex(teacher => teacher.id == details.user.id);
                if (index >= 0) this.teachers.splice(index, 1);

                usersTable.updateData();

                context.modal.deleteHandled();
              }
            },
            error: error => {
              context.modal.deleteHandled($localize`:Notifications|Contest enrollment delete has failed message@@notifications-contest-enrollment-delete-failed-error:Contest enrollment delete has failed`, error);
            }
          });
        }
      });
    }
  }

  handleRegionSelected(region: Region) {
    if (this.region && this.region.id != region.id) {
      this.school = undefined;
    }

    this.region = region;
    this.loadPendingApproval(this.competitionId, this.contest, this.region, this.school);
    this.loadTeachers(this.competitionId, this.contest, this.region, this.school, true);
  }

  handleSchoolSelected(schoolSelectResult: SchoolSelectResult) {
    const school = schoolSelectResult ? schoolSelectResult.school : undefined;
    this.schoolType = schoolSelectResult ? schoolSelectResult.type : undefined;

    if (this.school != school) {
      this.school = school;
      this.loadPendingApproval(this.competitionId, this.contest, this.region, this.school);
      this.loadTeachers(this.competitionId, this.contest, this.region, this.school, true);
    }
  }

  handleRequestUpdate(details: UserRequestDetails) {
    this.contestService.updateRequest(details.contest.id, details.user.id, this.TEACHER_ROLE, details.approve).subscribe({
      next: user => {
        if (this.pendingRequests) {
          const index = this.pendingRequests.findIndex(u => u.id == user.id);
          if (index >= 0) {
            this.pendingRequests.splice(index, 1);
          }

          if (index < 0 || this.pendingRequests.length == 0) {
            this.loadTeachers(this.competitionId, this.contest, this.region, this.school, true);
          } else if (this.teachers) {
            this.teachers.unshift(user);
          }
        }
      },
      error: error => {
        NotificationHandler.handleError($localize`:Notifications|Contest enrol request update has failed message@@notifications-contest-enrol-request-update-failed-error:Contest enrol request update has failed`, error);
      }
    });
  }
}
